import { Link, useNavigate } from "react-router-dom";
import { inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Zap, Package, Layers, Boxes, Clock } from "lucide-react";

const TYPE_META = {
  DIGITAL: { label: "Digital", icon: Zap, cls: "bg-teal-50 text-teal-700 border-teal-200" },
  PHYSICAL: { label: "Physical", icon: Package, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  HYBRID: { label: "Digital + Physical", icon: Layers, cls: "bg-violet-50 text-violet-700 border-violet-200" },
  BUNDLE: { label: "Bundle", icon: Boxes, cls: "bg-amber-500/10 text-amber-700 border-amber-500/30" },
};

export default function ProductCard({ product }) {
  const { add } = useCart();
  const navigate = useNavigate();
  const isUpcoming = ["UPCOMING", "PRE_LAUNCH"].includes(product.status);
  const meta = TYPE_META[product.type] || TYPE_META.DIGITAL;
  const Icon = isUpcoming ? Clock : meta.icon;

  return (
    <div className="group bg-white border border-slate-200/80 rounded-xl shadow-sm hover:shadow-md hover:border-teal-500/50 transition-all duration-200 overflow-hidden flex flex-col" data-testid={`product-card-${product.slug}`}>
      <Link to={`/product/${product.slug}`} className="block relative aspect-[4/3] overflow-hidden bg-slate-100">
        {product.cover_image ? (
          <img src={product.cover_image} alt={product.name} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : <div className="w-full h-full bg-slate-200" />}
        <div className="absolute top-3 left-3 flex gap-2">
          <Badge className={`${isUpcoming ? "bg-navy text-white border-navy" : meta.cls} border font-medium gap-1`}>
            <Icon className="w-3 h-3" /> {isUpcoming ? "Coming Soon" : meta.label}
          </Badge>
        </div>
        {product.on_offer && !isUpcoming && (
          <Badge className="absolute top-3 right-3 bg-gold text-white border-gold">Offer</Badge>
        )}
      </Link>
      <div className="p-4 sm:p-5 flex flex-col flex-1">
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-heading font-semibold text-navy text-base leading-snug hover:text-teal transition-colors">{product.name}</h3>
        </Link>
        <p className="text-sm text-slate-500 mt-1.5 line-clamp-2 flex-1">{product.short_description}</p>
        <div className="flex items-end justify-between mt-4">
          <div>
            {isUpcoming ? (
              <span className="text-sm font-medium text-slate-500">Launching soon</span>
            ) : (
              <div className="flex items-baseline gap-2">
                <span className="text-xl font-bold text-navy" data-testid={`price-${product.slug}`}>{inr(product.price)}</span>
                {product.compare_at && <span className="text-sm text-slate-400 line-through">{inr(product.compare_at)}</span>}
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          {isUpcoming ? (
            <Button variant="outline" className="w-full border-navy text-navy hover:bg-navy hover:text-white"
              onClick={() => navigate(`/product/${product.slug}`)} data-testid={`notify-btn-${product.slug}`}>
              Notify Me
            </Button>
          ) : (
            <>
              <Button variant="outline" className="flex-1 border-slate-300" onClick={() => add(product)} data-testid={`add-cart-${product.slug}`}>
                Add
              </Button>
              <Button className="flex-1 bg-teal hover:bg-teal-dark text-white gap-1" onClick={() => navigate(`/product/${product.slug}`)} data-testid={`view-btn-${product.slug}`}>
                View <ArrowRight className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
